import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Tooltip, forwardRef } from '@chakra-ui/react';
import { InfoIcon } from '@chakra-ui/icons';
import Button from 'components/v4/Button';
import Grid from 'components/v4/Grid';
import Input from 'components/v4/Input';
import FormLabel from 'components/v4/FormLabel';
import { Select as _MultiSelect, Select as _Select } from 'chakra-react-select';
import { multiSelectDataMapper, query, toSentenceCase } from 'utils/utils';
import style from './styles';
import FooterContainer from './FooterContainer';
import Heading from 'components/v4/Heading';
import Alert from 'components/v4/Alert';
import AlertDescription from 'components/v4/AlertDescription';
import AlertIcon from 'components/v4/AlertIcon';
import createServiceProfile from 'API/ServiceProfiles/createServiceProfile';
import LoadingIndicator from 'components/v4/LoadingIndicator';

import useFieldValidation from 'hooks/useFieldValidation';
import FieldValidation from 'components/v4/FieldValidation';

import { validationSchemaOCC } from './schema';
import { axios } from '@/services/axios';
import getServiceProfiles from 'API/getServiceProfiles';
import updateServiceProfiles from 'API/ServiceProfiles/updateServiceProfiles';

import walkthroughIds from '../../walkthroughIds';
import { defaultChakraSelectStyle, chakraSelectDarkMode } from '@/constants';
import config from '@/config.json';

const MultiSelect = forwardRef((props, ref) => (
    <_MultiSelect selectedOptionColor="brand" ref={ref} {...props} />
));

const Select = forwardRef((props, ref) => (
    <_Select selectedOptionColor="brand" ref={ref} {...props} />
));
export default function OCC({
    mode,
    data,
    handleBackButton,
    level,
    visibleScopeOptions,
}) {
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(null);

    const [operatorsEnum, setOperatorsEnum] = useState([]);
    const [callingProfiles, setCallingProfiles] = useState([]);

    const { darkMode } = useSelector((state) => state.settings);
    const { requiredScope } = useSelector((state) => state.login);

    const {
        currentWholesaler: wholesalerID,
        currentPartner: partnerID,
        wholesalers,
        companiesAndPartners,
    } = useSelector((state) => state.navigationLists);

    const DEFAULT_OCC_PAYLOAD = {
        name: `Operator Connect Conferencing Wholesaler Profile`,
        serviceType: 'OperatorConnectConferencing',
        callingProfiles: [],
        operatorProfiles: [],
        partnerID: level === 'partner' ? partnerID : undefined,
        wholesalerID: level === 'partner' ? undefined : wholesalerID,
        visibleScope: level === 'partner' ? 20 : 60,
    };

    const { ...ufElements } = walkthroughIds.serviceProfiles;
    const chakraSelectDarkStyle = chakraSelectDarkMode(darkMode);
    const chakraStyles = {
        ...defaultChakraSelectStyle,
        ...chakraSelectDarkStyle,
    };

    const [modalState, setModalState] = useState(data || DEFAULT_OCC_PAYLOAD);
    const { validationErrors, validatefn } = useFieldValidation(
        modalState,
        validationSchemaOCC,
    );

    useEffect(() => {
        if (mode !== 'EDIT' && level !== 'partner' && wholesalers?.length > 0) {
            const wsName = wholesalers.find(
                ({ id, name }) => id === wholesalerID,
            )?.name;
            handleChangeState({
                name: `${wsName} - Operator Connect Conferencing Wholesaler Profile`,
            });
        } else if (
            mode !== 'EDIT' &&
            level === 'partner' &&
            wholesalers?.length > 0
        ) {
            const partnerName = companiesAndPartners.find(
                ({ id, name }) => id === partnerID,
            )?.name;
            handleChangeState({
                name: `${partnerName} - Operator Connect Conferencing Partner Profile`,
            });
        }
    }, [mode, companiesAndPartners, wholesalers, level]);

    /*
     * A function to get the current scope and append it for API calls
     */
    const scope = () => {
        if (level === 'partner') {
            return `Partner/${partnerID}`;
        } else {
            return `Wholesaler/${wholesalerID}`;
        }
    };

    // function to set modal state with a given payload
    const handleChangeState = (payload) => {
        setModalState((prev) => ({
            ...prev,
            ...payload,
        }));
    };

    /*
     * Makes sure that operator profiles has been defined and if it has ->
     * sets the CallingProilfes list as the result of a api call with Scope and Query
     * is recalled whenever operator profiles is changed as the drop down list changes with that information
     */
    useEffect(() => {
        if (modalState.operatorProfiles?.length > 0) {
            axios
                .get(
                    `/ServiceProfiles/OperatorConnectConferencing/${scope()}/CallingProfiles/Available?OperatorProfileIDs=${query(
                        modalState.operatorProfiles,
                    )}`,
                )
                .then((res) => {
                    const data = res.data.map(multiSelectDataMapper);
                    setCallingProfiles(data);
                })
                .catch((e) => {
                    setError(e);
                });
        }
    }, [modalState.operatorProfiles]);

    const handleGetDropdownData = async () => {
        setIsLoading(true);
        const fetchSPData = mode === 'EDIT' && data;
        try {
            const opResult = await axios.get(
                `/ServiceProfiles/OperatorConnectConferencing/${scope()}/OperatorProfiles/Available`,
            );
            setOperatorsEnum(opResult.data.map(multiSelectDataMapper));

            if (fetchSPData) {
                const spResult = await getServiceProfiles({
                    level,
                    id: data?.id,
                });
                const callingProfiles = spResult.data.callingProfiles?.map(
                    multiSelectDataMapper,
                );
                const operatorProfiles = spResult.data.operatorProfiles?.map(
                    multiSelectDataMapper,
                );

                setModalState({
                    ...spResult.data,
                    callingProfiles,
                    operatorProfiles,
                });
            }
        } catch (e) {
            setError(e);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        handleGetDropdownData();
    }, []);

    const handleSubmit = async () => {
        setError(null);
        //validate
        if (!validatefn()) {
            setError({
                response: {
                    data: {
                        message:
                            'There are some validation errors in the form. Please check and try again.',
                    },
                },
            });
            return;
        }
        setIsLoading(true);
        try {
            if (mode === 'EDIT') {
                const result = await updateServiceProfiles({
                    type: 'OperatorConnectConferencing',
                    payload: modalState,
                    level,
                    scope: requiredScope,
                });
                handleBackButton();
            } else {
                const result = await createServiceProfile({
                    type: 'OperatorConnectConferencing',
                    payload: modalState,
                    level,
                    scope: requiredScope,
                });
                handleBackButton();
            }
        } catch (e) {
            setError(e);
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading) {
        return <LoadingIndicator />;
    }

    return (
        <Box sx={{ maxWidth: '2xl', minWidth: 'xl', margin: 'auto' }}>
            <Heading fontSize="3xl" as="h2" sx={{ marginBottom: '10px' }}>
                {`${toSentenceCase(mode)} ${modalState.name || data?.name}`}
            </Heading>

            {error && (
                <Alert status="error" sx={{ marginTop: '1rem' }}>
                    <AlertIcon />
                    <AlertDescription>
                        {error?.response?.data?.message ||
                            'An error occured. Please try again later.'}
                    </AlertDescription>
                </Alert>
            )}

            <Box sx={style.fieldContainer}>
                <FormLabel sx={style.label}>Service name</FormLabel>
                <Input
                    value={modalState.name}
                    onChange={(e) =>
                        handleChangeState({ name: e.target.value })
                    }
                    sx={style.inputField}
                    data-walkthroughid={ufElements.ufServiceNameInput}
                />
                <FieldValidation errors={validationErrors?.name} />
            </Box>

            <Box sx={style.fieldContainer}>
                <FormLabel sx={style.label}>
                    Visible Scope &nbsp;
                    <Tooltip
                        label="Determine visibility and service creation level"
                        hasArrow
                        placement="top-end">
                        <InfoIcon />
                    </Tooltip>
                </FormLabel>
                {modalState.visibleScope <= requiredScope ? (
                    <Select
                        defaultValue={visibleScopeOptions.find(
                            (option) =>
                                option.value === modalState.visibleScope,
                        )}
                        options={visibleScopeOptions}
                        chakraStyles={chakraStyles}
                        onChange={(selectedOption) => {
                            handleChangeState({
                                visibleScope: selectedOption.value,
                            });
                        }}
                        selectedOptionColor="brand"
                    />
                ) : (
                    <Input
                        isDisabled
                        value={config.scope.json[modalState.visibleScope]}
                    />
                )}
            </Box>

            <Box
                sx={style.fieldContainer}
                data-walkthroughid={ufElements.ufOperatorsSelect}>
                <FormLabel sx={style.label}> Operators </FormLabel>
                <MultiSelect
                    isMulti
                    placeholder="- Select Operators -"
                    closeMenuOnSelect={false}
                    tagVariant="solid"
                    onChange={(selectedOptions) => {
                        handleChangeState({
                            operatorProfiles: selectedOptions,
                        });
                    }}
                    options={operatorsEnum}
                    defaultValue={modalState.operatorProfiles}
                    chakraStyles={chakraStyles}
                />
                <FieldValidation errors={validationErrors?.operatorProfiles} />
            </Box>
            <Box
                sx={style.fieldContainer}
                data-walkthroughid={ufElements.ufCallingProfilesSelect}>
                <FormLabel sx={style.label}> Calling Profiles </FormLabel>
                <MultiSelect
                    isMulti
                    placeholder="- Select Calling Profiles -"
                    closeMenuOnSelect={false}
                    tagVariant="solid"
                    onChange={(selectedOptions) => {
                        handleChangeState({
                            callingProfiles: selectedOptions,
                        });
                    }}
                    options={callingProfiles}
                    defaultValue={modalState.callingProfiles}
                    chakraStyles={chakraStyles}
                />
                <FieldValidation errors={validationErrors?.callingProfiles} />
            </Box>

            <FooterContainer>
                <Button
                    variant="outline"
                    sx={{ background: 'white' }}
                    onClick={handleBackButton}
                    data-walkthroughid={ufElements.ufCloseButton}>
                    Close
                </Button>
                <Button
                    data-walkthroughid={ufElements.ufUpdateButton}
                    onClick={handleSubmit}>
                    {mode === 'ADD' ? 'Add' : 'Update'}
                </Button>
            </FooterContainer>
        </Box>
    );
}
